// lineChart.ts

type Padding = { top: number; right: number; bottom: number; left: number }

export interface Series {
	name: string
	color: string // CSS color
	data: number[]
}

export interface ChartConfig {
	width?: number
	height?: number
	padding?: Padding
	title?: string
	xLabel?: string
	yLabel?: string
	xCategories: string[] // ex) ["데이터 1", ...]
	series: Series[] // ex) [{ name, color, data }]
	gridLines?: number // horizontal grid count
	showArea?: boolean // fill under lines
	curve?: 'linear' | 'smooth'
	smoothness?: number
}

export function renderLineChartSVG(mountTarget: string | HTMLElement, cfg: ChartConfig): void {
	const mount = typeof mountTarget === 'string' ? document.querySelector<HTMLElement>(mountTarget) : mountTarget
	if (!mount) throw new Error('Mount element not found')

	const {
		width = 1100,
		height = 600,
		padding = { top: 40, right: 40, bottom: 60, left: 46 },
		title = 'Line chart name',
		xLabel = '(x축 레이블)',
		yLabel = '(y축 레이블)',
		xCategories,
		series,
		gridLines = 6,
		showArea = true,
		curve = 'smooth',
		smoothness = 1,
	} = cfg

	// 초기화
	mount.innerHTML = ''
	mount.style.position = 'relative'

	const svgNS = 'http://www.w3.org/2000/svg'
	const svg = createEl('svg', {
		width: String(width),
		height: String(height),
		viewBox: `0 0 ${width} ${height}`,
		style: 'background:#fff;border-radius:4px;',
	})

	const root = createEl('g')
	svg.appendChild(root)

	// 제목
	root.appendChild(
		createEl(
			'text',
			{
				x: '20',
				y: '26',
				fill: '#6B6E79',
				'font-size': '20',
				'font-weight': '700',
				'font-family': 'ui-sans-serif, -apple-system, "Noto Sans KR", Arial',
			},
			title,
		),
	)

	// 플롯 영역
	const plotX = padding.left
	const plotY = padding.top
	const plotW = width - padding.left - padding.right
	const plotH = height - padding.top - padding.bottom

	const rectHeight = plotY + plotH + 50

	// 스케일
	const allY = series.flatMap(s => s.data)
	const yMaxData = Math.max(allY.length ? Math.max(...allY) : 10, 1)
	const yMax = niceMax(yMaxData)
	const yMin = 0

	const maxLen = Math.max(...series.map(s => s.data.length))
	const xCats = cfg.xCategories && cfg.xCategories.length ? cfg.xCategories : Array.from({ length: maxLen }, (_, i) => `데이터 ${i + 1}`)
	const xCount = Math.max(xCategories.length, 1)
	const xStep = plotW / Math.max(xCount - 1, 1)
	const xScale = (i: number) => plotX + i * xStep
	const yScale = (v: number) => plotY + 65 + (yMax - v) * ((plotH - 60) / (yMax - yMin))

	// 수평 그리드 + y축 눈금
	const effectiveGridLines = Math.min(gridLines, yMax)
	const grid = createEl('g')
	root.appendChild(grid)
	for (let i = 0; i <= effectiveGridLines; i++) {
		const v = yMax * (i / effectiveGridLines)
		const y = yScale(v)
		grid.appendChild(
			createEl('line', {
				x1: String(plotX),
				y1: String(y),
				x2: String(plotX + plotW),
				y2: String(y),
				stroke: '#DDE1E7',
				'stroke-dasharray': '4 4',
			}),
		)
		grid.appendChild(
			createEl(
				'text',
				{
					x: String(plotX - 10),
					y: String(y + 5),
					'text-anchor': 'end',
					fill: '#6B6E79',
					'font-size': '12',
					'font-family': 'ui-sans-serif, "Noto Sans KR", Arial',
				},
				String(Math.round(v)),
			),
		)
	}

	// 라벨
	root.appendChild(
		createEl(
			'text',
			{
				x: String(20),
				y: String(plotY + 48),
				fill: '#6B6E79',
				'font-size': '12',
				'font-weight': '600',
			},
			yLabel,
		),
	)

	root.appendChild(
		createEl(
			'text',
			{
				x: String(plotX + plotW),
				y: String(rectHeight),
				'text-anchor': 'end',
				fill: '#6B6E79',
				'font-size': '12',
				'font-weight': '600',
			},
			xLabel,
		),
	)

	// x축 카테고리 + 세로 보조선
	const xTicks = createEl('g')
	root.appendChild(xTicks)
	xCats.forEach((lab, i) => {
		const x = xScale(i)
		xTicks.appendChild(
			createEl('line', {
				x1: String(x),
				y1: String(plotY + 65),
				x2: String(x),
				y2: String(rectHeight - 45),
				stroke: '#DDE1E7',
				'stroke-dasharray': '4 4',
			}),
		)
		xTicks.appendChild(
			createEl(
				'text',
				{
					x: String(x),
					y: String(plotY + plotH + 30),
					'text-anchor': 'middle',
					fill: '#111827',
					'font-size': '12',
					'font-family': 'ui-sans-serif, "Noto Sans KR", Arial',
				},
				lab,
			),
		)
	})

	// 범례
	const legend = createEl('g')
	root.appendChild(legend)
	let lx = 20
	const ly = 48
	series.forEach((s, idx) => {
		const group = createEl('g')
		legend.appendChild(group)
		group.appendChild(
			createEl('rect', {
				x: String(lx),
				y: String(ly - 10),
				width: '14',
				height: '14',
				rx: '3',
				fill: s.color,
			}),
		)
		group.appendChild(
			createEl(
				'text',
				{
					x: String(lx + 20),
					y: String(ly + 2),
					fill: '#374151',
					'font-size': '12',
				},
				s.name ?? `시리즈${idx + 1}`,
			),
		)
		lx += 90
	})

	// 테두리
	root.appendChild(
		createEl('line', {
			x1: String(plotX),
			y1: String(plotY + 65),
			x2: String(plotX),
			y2: String(rectHeight - 45),
			stroke: '#6B6E79',
		}),
	)
	root.appendChild(
		createEl('line', {
			x1: String(plotX),
			y1: String(rectHeight - 45),
			x2: String(plotX + plotW),
			y2: String(rectHeight - 45),
			stroke: '#6B6E79',
		}),
	)

	mount.appendChild(svg)

	// ======= Tooltip =======
	const tooltip = document.createElement('div')
	tooltip.style.position = 'absolute'
	tooltip.style.zIndex = '10'
	tooltip.style.pointerEvents = 'none'
	tooltip.style.padding = '6px 8px'
	tooltip.style.background = '#111827'
	tooltip.style.color = '#fff'
	tooltip.style.borderRadius = '4px'
	tooltip.style.fontSize = '12px'
	tooltip.style.lineHeight = '1.2'
	tooltip.style.whiteSpace = 'nowrap'
	tooltip.style.boxShadow = '0 4px 12px rgba(0,0,0,0.18)'
	tooltip.style.opacity = '0'
	mount.appendChild(tooltip)

	const showTooltip = (html: string) => {
		tooltip.innerHTML = html
		tooltip.style.opacity = '1'
	}
	const moveTooltip = (e: MouseEvent) => {
		const r = mount.getBoundingClientRect()
		let x = e.clientX - r.left + 8
		let y = e.clientY - r.top - 12
		tooltip.style.left = '0px'
		tooltip.style.top = '-1000px'
		const tw = tooltip.offsetWidth || 0
		const th = tooltip.offsetHeight || 0
		if (x + tw + 8 > r.width) x = r.width - tw - 8
		if (x < 4) x = 4
		if (y < 4) y = e.clientY - r.top + 16
		if (y + th + 4 > r.height) y = r.height - th - 4
		tooltip.style.left = `${x}px`
		tooltip.style.top = `${y}px`
	}
	const hideTooltip = () => {
		tooltip.style.opacity = '0'
	}

	// ======= 내부 유틸 =======
	function createEl<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, text?: string): SVGElementTagNameMap[K] {
		const el = document.createElementNS(svgNS, tag) as SVGElementTagNameMap[K]
		Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v))
		if (text != null) el.textContent = text
		return el
	}

	function niceMax(v: number): number {
		const pow = Math.pow(10, Math.floor(Math.log10(v)))
		const n = v / pow
		const step = n <= 2 ? 2 : n <= 5 ? 5 : 10
		return step * pow
	}
	type Pt = [number, number] | null
	const pointsBySeries: Pt[][] = series.map(s => Array.from({ length: xCount }, (_, i) => (typeof s.data[i] === 'number' ? [xScale(i), yScale(s.data[i] as number)] : null)))

	pointsBySeries.forEach((pts, si) => {
		const color = series[si].color
		const fillColor: any = hexToRgb(color)
		pts.forEach((p, i) => {
			if (!p) return
			const outer = createEl('circle', {
				cx: String(p[0]),
				cy: String(p[1]),
				r: '8',
				fill: `rgba(${fillColor.r}, ${fillColor.g}, ${fillColor.b}, 0.4)`,
			})
			root.appendChild(outer)
			const inner = createEl('circle', {
				cx: String(p[0]),
				cy: String(p[1]),
				r: '4',
				fill: `rgba(${fillColor.r}, ${fillColor.g}, ${fillColor.b}, 0.8)`,
			})
			// Tooltip 이벤트 (작은 점에 부착)
			const seriesName = series[si]?.name ?? `시리즈${si + 1}`
			const categoryLabel = xCats[i] ?? `데이터 ${i + 1}`
			const value = typeof series[si]?.data?.[i] === 'number' ? (series[si].data[i] as number) : 0
			// 바깥 원에도 동일 이벤트 부착 (mouseenter는 버블링 안 됨)
			;(outer as any).dataset.series = String(si)
			;(outer as any).dataset.categoryIndex = String(i)
			;(outer as any).dataset.value = String(value)
			outer.addEventListener('mouseenter', (e: MouseEvent) => {
				showTooltip(`<div style="font-weight:700;margin-bottom:2px;">${categoryLabel}</div><div>${seriesName}: <b>${value}</b></div>`)
				moveTooltip(e)
			})
			outer.addEventListener('mousemove', (e: MouseEvent) => moveTooltip(e))
			outer.addEventListener('mouseleave', () => hideTooltip())
			;(inner as any).dataset.series = String(si)
			;(inner as any).dataset.categoryIndex = String(i)
			;(inner as any).dataset.value = String(value)
			inner.addEventListener('mouseenter', (e: MouseEvent) => {
				showTooltip(`<div style="font-weight:700;margin-bottom:2px;">${categoryLabel}</div><div>${seriesName}: <b>${value}</b></div>`)
				moveTooltip(e)
			})
			inner.addEventListener('mousemove', (e: MouseEvent) => moveTooltip(e))
			inner.addEventListener('mouseleave', () => hideTooltip())
			root.appendChild(inner)
		})
	})

	function splitByNull(pts: Pt[]): Array<[number, number][]> {
		const segs: Array<[number, number][]> = []
		let cur: Array<[number, number]> = []
		for (const p of pts) {
			if (p) cur.push(p)
			else {
				if (cur.length) segs.push(cur)
				cur = []
			}
		}
		if (cur.length) segs.push(cur)
		return segs
	}

	pointsBySeries.forEach((pts: any, idx) => {
		const color = series[idx].color
		const d = curve === 'smooth' ? linePathSmooth(pts, smoothness) : linePathLinear(pts)
		root.appendChild(
			createEl('path', {
				d,
				fill: 'none',
				stroke: color,
				'stroke-width': '0.4',
				class: 'animation--chart',
				'pointer-events': 'none',
			}),
		)
	})

	pointsBySeries.forEach((pts: any, idx) => {
		const color = series[idx].color
		const fillColor: any = hexToRgb(color)
		const d = curve === 'smooth' ? linePathSmooth(pts, smoothness) : linePathLinear(pts)
		root.appendChild(
			createEl('path', {
				d,
				fill: 'none',
				stroke: `rgba(${fillColor.r}, ${fillColor.g}, ${fillColor.b}, 0.07)`,
				'stroke-width': '10',
				class: 'animation--chart',
				'pointer-events': 'none',
			}),
		)
	})

	function linePathLinear(pts: Array<[number, number]>): string {
		return pts.map(([x, y], i) => (i ? 'L' : 'M') + x + ' ' + y).join(' ')
	}

	function linePathSmooth(pts: Array<[number, number]>, t = 0.5): string {
		if (pts.length <= 1) return pts.length ? `M ${pts[0][0]} ${pts[0][1]}` : ''
		if (pts.length === 2) return linePathLinear(pts)
		let d = `M ${pts[0][0]} ${pts[0][1]}`
		for (let i = 0; i < pts.length - 1; i++) {
			const p0 = pts[i - 1] ?? pts[i]
			const p1 = pts[i]
			const p2 = pts[i + 1]
			const p3 = pts[i + 2] ?? pts[i + 1]
			const c1x = p1[0] + (p2[0] - p0[0]) * (t / 6)
			const c1y = p1[1] + (p2[1] - p0[1]) * (t / 6)
			const c2x = p2[0] - (p3[0] - p1[0]) * (t / 6)
			const c2y = p2[1] - (p3[1] - p1[1]) * (t / 6)
			d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2[0]} ${p2[1]}`
		}
		return d
	}

	const useSmooth = (cfg.curve ?? 'smooth') === 'smooth'
	const t = cfg.smoothness ?? 1

	if (cfg.showArea ?? true) {
		pointsBySeries.forEach((pts, si) => {
			const segs = splitByNull(pts)
			segs.forEach(seg => {
				const top = useSmooth ? linePathSmooth(seg, t) : linePathLinear(seg)
				const firstX = seg[0][0]
				const lastX = seg[seg.length - 1][0]
				const d = areaFromTopPath(top, firstX, lastX, yScale(0))
				root.appendChild(createEl('path', { d, fill: series[si].color, opacity: '0.1', class: 'animation--chart', 'pointer-events': 'none' }))
			})
		})
	}

	function areaFromTopPath(topD: string, firstX: number, lastX: number, baseY: number): string {
		return `${topD} L ${lastX} ${baseY} L ${firstX} ${baseY} Z`
	}

	function hexToRgb(hex: string) {
		if (!hex || !/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
			return null
		}
		let hexValue = hex.substring(1)
		if (hexValue.length === 3) {
			hexValue = hexValue
				.split('')
				.map(char => char + char)
				.join('')
		}
		const bigint = parseInt(hexValue, 16)
		return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 }
	}
}

/* ================= 예시 호출 =================
import { renderLineChartSVG } from "./lineChart";

renderLineChartSVG("#chart", {
  title: "Line chart name",
  xLabel: "(x축 레이블)",
  yLabel: "(y축 레이블)",
  xCategories: ["데이터 1", "데이터 2", "데이터 3", "데이터 4", "데이터 5"],
  series: [
    { name: "라벨1번", color: "#9CA3AF", data: [10, 6, 2, 7, 9] },
    { name: "라벨2번", color: "#22C1D6", data: [3, 5, 4, 8, 6] },
    { name: "라벨3번", color: "#F4C542", data: [5, 6, 1.5, 2, 3.5] },
    { name: "라벨4번", color: "#A78BFA", data: [11, 5.8, 9, 8.5, 10] }
  ],
  showArea: true
});
================================================ */
